import { HttpErrorResponse } from '@angular/common/http';
import { IPersonDTO } from '@modeso/types__aumico-lib-companies-be';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import * as fromPersonsActions from '../actions/persons.actions';
import { PersonsFilter } from '../interfaces/persons-filter.interface';

/**
 * REV-22155 — store of the persons: the list (entities), the selected person (profile), the
 * remembered overview filter, the persons linked to the open company, and the last error.
 * Selectors live in this file, like companies.
 */
export const personsFeatureKey = 'aumicoPersons';

export interface PersonsFeatureState extends EntityState<IPersonDTO> {
    selectedPerson: IPersonDTO | null;
    /** "Verknüpfte Personen" of the company profile — separate, so it never overwrites the list. */
    personsOfCompany: IPersonDTO[];
    filter: PersonsFilter;
    error: HttpErrorResponse | null;
}

export const personsAdapter: EntityAdapter<IPersonDTO> = createEntityAdapter<IPersonDTO>({
    selectId: (person: IPersonDTO) => person._id,
});

export const initialPersonsState: PersonsFeatureState = personsAdapter.getInitialState({
    selectedPerson: null,
    personsOfCompany: [],
    filter: {},
    error: null,
});

const { selectAll, selectEntities } = personsAdapter.getSelectors();

export const selectPersonsFeature = createFeatureSelector<PersonsFeatureState>(personsFeatureKey);
export const selectAllPersons = createSelector(selectPersonsFeature, selectAll);
export const selectPersonsEntities = createSelector(selectPersonsFeature, selectEntities);
export const selectSelectedPerson = createSelector(selectPersonsFeature, (state) => state.selectedPerson);
export const selectPersonsOfCompany = createSelector(selectPersonsFeature, (state) => state.personsOfCompany);
export const selectPersonsFilter = createSelector(selectPersonsFeature, (state) => state.filter);
export const selectPersonsError = createSelector(selectPersonsFeature, (state) => state.error);

const clearError = (state: PersonsFeatureState): PersonsFeatureState => ({ ...state, error: null });
const setError = (state: PersonsFeatureState, { payload }: { payload: HttpErrorResponse }): PersonsFeatureState => ({
    ...state,
    error: payload,
});

export const personsReducer = createReducer(
    initialPersonsState,

    on(fromPersonsActions.getPersons, clearError),
    on(fromPersonsActions.onGettingPersonsSuccessfully, (state, { payload }) =>
        personsAdapter.setAll(payload, { ...state, error: null }),
    ),
    on(fromPersonsActions.onGettingPersonsFailed, setError),

    on(fromPersonsActions.getPersonsOfCompany, (state) => ({ ...state, personsOfCompany: [], error: null })),
    on(fromPersonsActions.onGettingPersonsOfCompanySuccessfully, (state, { payload }) => ({
        ...state,
        personsOfCompany: payload,
    })),
    on(fromPersonsActions.onGettingPersonsOfCompanyFailed, setError),

    on(fromPersonsActions.getPersonById, (state) => ({ ...state, selectedPerson: null, error: null })),
    on(fromPersonsActions.onGettingPersonByIdSuccessfully, (state, { payload }) =>
        personsAdapter.upsertOne(payload, { ...state, selectedPerson: payload }),
    ),
    on(fromPersonsActions.onGettingPersonByIdFailed, setError),

    // After creating, the page navigates to the new person — it reads the id from `selectedPerson`.
    on(fromPersonsActions.createPerson, clearError),
    on(fromPersonsActions.onCreatePersonSuccessfully, (state, { payload }) =>
        personsAdapter.addOne(payload, { ...state, selectedPerson: payload }),
    ),
    on(fromPersonsActions.onCreatePersonFailed, setError),

    on(fromPersonsActions.updatePerson, clearError),
    on(fromPersonsActions.changePersonStatus, clearError),
    on(fromPersonsActions.onUpdatePersonSuccessfully, fromPersonsActions.onChangePersonStatusSuccessfully, (state, { payload }) =>
        personsAdapter.upsertOne(payload, { ...state, selectedPerson: payload }),
    ),
    on(fromPersonsActions.onUpdatePersonFailed, fromPersonsActions.onChangePersonStatusFailed, setError),

    on(fromPersonsActions.deletePerson, clearError),
    on(fromPersonsActions.onDeletePersonSuccessfully, (state, { id }) =>
        personsAdapter.removeOne(id, {
            ...state,
            selectedPerson: state.selectedPerson?._id === id ? null : state.selectedPerson,
        }),
    ),
    on(fromPersonsActions.onDeletePersonFailed, setError),

    on(fromPersonsActions.bulkUpdatePersons, clearError),
    on(fromPersonsActions.onBulkUpdatePersonsSuccessfully, (state, { payload }) =>
        personsAdapter.upsertMany(payload, state),
    ),
    on(fromPersonsActions.onBulkUpdatePersonsFailed, setError),

    on(fromPersonsActions.setPersonsFilter, (state, { filter }) => ({ ...state, filter })),

    on(fromPersonsActions.exportPersonsDataAsExcel.do, clearError),
    on(fromPersonsActions.exportPersonsDataAsExcel.failed, setError),

    on(fromPersonsActions.resetPersons, () => initialPersonsState),
    on(fromPersonsActions.resetPersonsErrors, clearError),
);

export function personsReducerFn(state: PersonsFeatureState | undefined, action: Action) {
    return personsReducer(state, action);
}
